import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import Navbar from './Navbar';
import {
    KOT_PRINTER_MODES,
    fetchPrintBehaviorSettings,
    getDefaultPrintBehaviorSettings,
    getPrintBehaviorSettings,
    resetPrintBehaviorSettings,
    savePrintBehaviorSettings
} from '../../utils/printBehaviorSettings';

const SETTING_META = [
    {
        key: 'kot',
        title: 'KOT Print',
        description: 'Kitchen Order Ticket print enable/disable.'
    },
    {
        key: 'separateItemKot',
        title: 'Print Separate KOT for Each Item',
        description: 'Har order item line ki alag KOT slip print hogi; quantity usi ek slip par rahegi.'
    },
    {
        key: 'completeOrder',
        title: 'Complete Order Print',
        description: 'Running/complete order slip print enable/disable.'
    },
    {
        key: 'tempBill',
        title: 'Temporary Bill Print',
        description: 'Temporary bill print enable/disable.'
    },
    {
        key: 'finalBill',
        title: 'Final Bill Print',
        description: 'Final checkout bill print enable/disable.'
    },
    {
        key: 'deliveryNote',
        title: 'Delivery Note Print',
        description: 'Regular delivery checkout par rider Delivery Note print enable/disable.'
    },
    {
        key: 'deliveryBarcode',
        title: 'Delivery Barcode',
        description: 'Delivery KOT aur Delivery Note par barcode enable/disable.'
    }
];

const PrintBehaviorSettings = () => {
    const [settings, setSettings] = useState(() => getPrintBehaviorSettings());
    const [bannerSettings, setBannerSettings] = useState({
        isEnabled: true,
        imageEnabled: true,
        textEnabled: false,
        imagePath: null,
        bannerText: '',
        textAlign: 'center',
        textSize: 'normal',
        textBold: true,
        startDate: '',
        endDate: '',
        isActiveNow: false
    });
    const [bannerImage, setBannerImage] = useState(null);
    const [bannerLoading, setBannerLoading] = useState(true);
    const [bannerSaving, setBannerSaving] = useState(false);
    const bannerPreviewUrl = useMemo(() => {
        if (bannerImage) return URL.createObjectURL(bannerImage);
        return bannerSettings.imagePath
            ? `${global.SET_API_URL}finalBillBanner/image?v=${encodeURIComponent(bannerSettings.imagePath)}`
            : null;
    }, [bannerImage, bannerSettings.imagePath]);

    useEffect(() => () => {
        if (bannerImage && bannerPreviewUrl) URL.revokeObjectURL(bannerPreviewUrl);
    }, [bannerImage, bannerPreviewUrl]);

    useEffect(() => {
        const loadBannerSettings = async () => {
            try {
                const response = await axios.get(`${global.SET_API_URL}finalBillBanner/`);
                const data = response.data?.data || {};
                setBannerSettings({
                    isEnabled: Boolean(data.isEnabled),
                    imageEnabled: Boolean(data.imageEnabled),
                    textEnabled: Boolean(data.textEnabled),
                    imagePath: data.imagePath || null,
                    bannerText: data.bannerText || '',
                    textAlign: data.textAlign || 'center',
                    textSize: data.textSize || 'normal',
                    textBold: Boolean(data.textBold),
                    startDate: data.startDate || '',
                    endDate: data.endDate || '',
                    isActiveNow: Boolean(data.isActiveNow)
                });
            } catch (error) {
                alert(error.response?.data?.message || 'Final bill banner settings load nahi ho sakin.');
            } finally {
                setBannerLoading(false);
            }
        };
        loadBannerSettings();
    }, []);

    const handleSaveBanner = async () => {
        if (bannerSettings.startDate && bannerSettings.endDate && bannerSettings.startDate > bannerSettings.endDate) {
            alert('Start date end date ke baad nahi ho sakti.');
            return;
        }
        const formData = new FormData();
        formData.append('isEnabled', bannerSettings.isEnabled ? 'true' : 'false');
        formData.append('imageEnabled', bannerSettings.imageEnabled ? 'true' : 'false');
        formData.append('textEnabled', bannerSettings.textEnabled ? 'true' : 'false');
        formData.append('bannerText', bannerSettings.bannerText || '');
        formData.append('textAlign', bannerSettings.textAlign || 'center');
        formData.append('textSize', bannerSettings.textSize || 'normal');
        formData.append('textBold', bannerSettings.textBold ? 'true' : 'false');
        formData.append('startDate', bannerSettings.startDate || '');
        formData.append('endDate', bannerSettings.endDate || '');
        if (bannerImage) formData.append('image', bannerImage);
        setBannerSaving(true);
        try {
            const response = await axios.post(`${global.SET_API_URL}finalBillBanner/`, formData);
            const data = response.data?.data || {};
            setBannerSettings((prev) => ({ ...prev, ...data, startDate: data.startDate || '', endDate: data.endDate || '' }));
            setBannerImage(null);
            alert('Final bill banner settings save ho gayi hain.');
        } catch (error) {
            alert(error.response?.data?.message || 'Final bill banner settings save nahi ho sakin.');
        } finally {
            setBannerSaving(false);
        }
    };

    useEffect(() => {
        fetchPrintBehaviorSettings()
            .then(setSettings)
            .catch(() => {});
    }, []);

    const hasChanges = useMemo(() => {
        const live = getPrintBehaviorSettings();
        return JSON.stringify(live) !== JSON.stringify(settings);
    }, [settings]);

    const handleToggle = (key) => (_event, checked) => {
        setSettings((prev) => ({
            ...prev,
            [key]: checked
        }));
    };

    const handleKotPrinterModeChange = (event) => {
        setSettings((prev) => ({
            ...prev,
            kotPrinterMode: event.target.value
        }));
    };

    const handleSave = async () => {
        try {
            const saved = await savePrintBehaviorSettings(settings);
            setSettings(saved);
            alert('Central print behavior settings saved successfully.');
        } catch (error) {
            alert(error?.response?.data?.message || 'Print behavior settings save nahi ho sakin.');
        }
    };

    const handleReset = async () => {
        try {
            const defaults = await resetPrintBehaviorSettings();
            setSettings(defaults);
            alert('Central print behavior settings reset to default values.');
        } catch (error) {
            alert(error?.response?.data?.message || 'Print behavior defaults save nahi ho sake.');
        }
    };

    const handleApplyDefaultsWithoutSave = () => {
        setSettings(getDefaultPrintBehaviorSettings());
    };

    return (
        <>
            <Navbar />
            <Box sx={{ p: 2 }}>
                <Card sx={{ maxWidth: 900, mx: 'auto' }}>
                    <CardContent>
                        <Typography variant="h5" fontWeight={700} gutterBottom>
                            Print Behavior Settings
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Enable only the print jobs you want. Disabled options are skipped during POS print flow.
                        </Typography>

                        <Divider sx={{ mb: 2 }} />

                        <Stack spacing={1.5}>
                            {SETTING_META.map((item) => (
                                <Box
                                    key={item.key}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1.5,
                                        p: 1.5
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={Boolean(settings[item.key])}
                                                onChange={handleToggle(item.key)}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="subtitle1" fontWeight={600}>
                                                    {item.title}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {item.description}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                    {item.key === 'kot' && (
                                        <FormControl size="small" sx={{ mt: 1, minWidth: 260 }} disabled={!settings.kot}>
                                            <InputLabel>KOT Printer Mode</InputLabel>
                                            <Select
                                                label="KOT Printer Mode"
                                                value={settings.kotPrinterMode || KOT_PRINTER_MODES.NETWORK}
                                                onChange={handleKotPrinterModeChange}
                                            >
                                                <MenuItem value={KOT_PRINTER_MODES.NETWORK}>Network Printer</MenuItem>
                                                <MenuItem value={KOT_PRINTER_MODES.SYSTEM}>System Installed Printer</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}
                                </Box>
                            ))}
                        </Stack>

                        <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                            <Button variant="contained" onClick={handleSave}>
                                Save Settings
                            </Button>
                            <Button variant="outlined" onClick={handleApplyDefaultsWithoutSave}>
                                Load Default (Unsaved)
                            </Button>
                            <Button variant="outlined" color="error" onClick={handleReset}>
                                Reset & Save Default
                            </Button>
                            <Typography variant="caption" sx={{ alignSelf: 'center', color: hasChanges ? 'warning.main' : 'success.main' }}>
                                {hasChanges ? 'Unsaved changes' : 'Saved'}
                            </Typography>
                        </Stack>

                        <Divider sx={{ my: 3 }} />
                        <Typography variant="h6" fontWeight={700}>
                            Final Bill Banner
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Banner ko enable/disable karein, PNG image replace karein aur optional print date range set karein.
                        </Typography>

                        <Stack spacing={2} sx={{ opacity: bannerLoading ? 0.6 : 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={Boolean(bannerSettings.isEnabled)}
                                        onChange={(_event, checked) => setBannerSettings((prev) => ({ ...prev, isEnabled: checked }))}
                                        disabled={bannerLoading}
                                    />
                                }
                                label={`Banner Print ${bannerSettings.isEnabled ? 'Enabled' : 'Disabled'}`}
                            />

                            <Stack direction="row" spacing={3} flexWrap="wrap">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={Boolean(bannerSettings.imageEnabled)}
                                            onChange={(_event, checked) => setBannerSettings((prev) => ({ ...prev, imageEnabled: checked }))}
                                            disabled={!bannerSettings.isEnabled}
                                        />
                                    }
                                    label="Print Image"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={Boolean(bannerSettings.textEnabled)}
                                            onChange={(_event, checked) => setBannerSettings((prev) => ({ ...prev, textEnabled: checked }))}
                                            disabled={!bannerSettings.isEnabled}
                                        />
                                    }
                                    label="Print Text"
                                />
                            </Stack>

                            <Box sx={{ display: bannerSettings.textEnabled ? 'block' : 'none' }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    label="Banner Text"
                                    value={bannerSettings.bannerText}
                                    onChange={(event) => setBannerSettings((prev) => ({ ...prev, bannerText: event.target.value.slice(0, 500) }))}
                                    helperText={`${bannerSettings.bannerText.length}/500 characters; multiple lines supported`}
                                />
                                <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap">
                                    <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <InputLabel>Text Alignment</InputLabel>
                                        <Select
                                            label="Text Alignment"
                                            value={bannerSettings.textAlign}
                                            onChange={(event) => setBannerSettings((prev) => ({ ...prev, textAlign: event.target.value }))}
                                        >
                                            <MenuItem value="left">Left</MenuItem>
                                            <MenuItem value="center">Center</MenuItem>
                                            <MenuItem value="right">Right</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <InputLabel>Text Size</InputLabel>
                                        <Select
                                            label="Text Size"
                                            value={bannerSettings.textSize}
                                            onChange={(event) => setBannerSettings((prev) => ({ ...prev, textSize: event.target.value }))}
                                        >
                                            <MenuItem value="normal">Normal</MenuItem>
                                            <MenuItem value="large">Large</MenuItem>
                                            <MenuItem value="extra">Extra Large</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={Boolean(bannerSettings.textBold)}
                                                onChange={(_event, checked) => setBannerSettings((prev) => ({ ...prev, textBold: checked }))}
                                            />
                                        }
                                        label="Bold Text"
                                    />
                                </Stack>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <TextField
                                    type="date"
                                    size="small"
                                    label="Start Date"
                                    value={bannerSettings.startDate}
                                    onChange={(event) => setBannerSettings((prev) => ({ ...prev, startDate: event.target.value }))}
                                    InputLabelProps={{ shrink: true }}
                                    helperText="Blank = immediately active"
                                />
                                <TextField
                                    type="date"
                                    size="small"
                                    label="End Date"
                                    value={bannerSettings.endDate}
                                    onChange={(event) => setBannerSettings((prev) => ({ ...prev, endDate: event.target.value }))}
                                    InputLabelProps={{ shrink: true }}
                                    helperText="End date ke baad auto stop"
                                />
                            </Box>

                            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ display: bannerSettings.imageEnabled ? 'flex' : 'none' }}>
                                <Button variant="outlined" component="label">
                                    Choose PNG Banner
                                    <input
                                        hidden
                                        type="file"
                                        accept="image/png"
                                        onChange={(event) => setBannerImage(event.target.files?.[0] || null)}
                                    />
                                </Button>
                                <Typography variant="body2">
                                    {bannerImage ? bannerImage.name : (bannerSettings.imagePath || 'No banner image')}
                                </Typography>
                            </Stack>

                            {bannerSettings.imageEnabled && (bannerImage || bannerSettings.imagePath) && (
                                <Box
                                    component="img"
                                    alt="Final bill banner preview"
                                    src={bannerPreviewUrl}
                                    sx={{ maxWidth: 520, width: '100%', maxHeight: 220, objectFit: 'contain', border: '1px solid', borderColor: 'divider' }}
                                />
                            )}

                            <Typography variant="body2" color={bannerSettings.isActiveNow ? 'success.main' : 'warning.main'}>
                                Current server status: {bannerSettings.isActiveNow ? 'Banner will print' : 'Banner will not print'}
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={handleSaveBanner}
                                disabled={bannerLoading || bannerSaving}
                                sx={{ alignSelf: 'flex-start' }}
                            >
                                {bannerSaving ? 'Saving...' : 'Save Banner Settings'}
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            </Box>
        </>
    );
};

export default PrintBehaviorSettings;

